import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Switch, 
  FormControlLabel, 
  CircularProgress, 
  Box 
} from '@mui/material';
import { useNotify } from 'react-admin';
import { formatISO9075 } from 'date-fns';

// Try to get the API URL from multiple sources
let API_BASE_URL;
try {
  // Check if we're running in a browser and have access to localStorage
  if (typeof window !== 'undefined' && window.localStorage) {
    // Try to parse API URL from auth config if it exists
    const authConfig = JSON.parse(localStorage.getItem('auth'));
    if (authConfig && authConfig.apiUrl) {
      API_BASE_URL = authConfig.apiUrl;
      console.log('Using API URL from localStorage:', API_BASE_URL);
    }
  }
} catch (e) {
  console.error('Error accessing localStorage:', e);
}

// Fallback to environment variables if localStorage didn't work
if (!API_BASE_URL) {
  API_BASE_URL = typeof import.meta !== 'undefined' && import.meta.env ? 
    (import.meta.env.VITE_API_URL || 'http://localhost:8001') :
    (process.env.NODE_ENV === 'development' ? 'http://localhost:8001' : 'https://test.paymadi.com');
  console.log('Using fallback API URL:', API_BASE_URL);
}

// Helper function to construct proper API URLs without duplication
const createApiUrl = (basePath, endpoint) => {
  // Check if basePath already ends with /admin/v1/api
  if (basePath.endsWith('/admin/v1/api')) {
    return `${basePath}/${endpoint}`;
  } 
  // Check if basePath already contains /admin/v1/api/ in the middle
  else if (basePath.includes('/admin/v1/api/')) {
    return `${basePath}/${endpoint}`;
  }
  // Otherwise, add the full path
  else {
    return `${basePath}/admin/v1/api/${endpoint}`;
  }
};

const StandaloneRechargeOperatorList = () => {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const notify = useNotify();

  // Format timestamps to readable date
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp * 1000); // Convert to milliseconds
    return formatISO9075(date);
  };

  // Toggle operator active status
  const toggleOperator = async (operatorId) => {
    try {
      console.log(`Toggling operator ${operatorId}`);
      // Find the currently active operator (other than the one being toggled)
      const activeOperator = operators.find(op => op.isActive && op.id !== operatorId);
      if (activeOperator) {
        // Disable the currently active operator
        let disableUrl;
        if (API_BASE_URL.startsWith('http')) {
          disableUrl = createApiUrl(API_BASE_URL, `rechargeOperator/${activeOperator.id}/false`);
        } else {
          disableUrl = `/admin/v1/api/rechargeOperator/${activeOperator.id}/false`;
        }
        console.log(`Disable URL: ${disableUrl}`);
        const disableResponse = await fetch(disableUrl, {
          method: 'PUT',
          headers: {
            'Authorization': localStorage.getItem('auth'),
            'Content-Type': 'application/json'
          }
        });
        if (!disableResponse.ok) {
          throw new Error(`Failed to disable operator: ${disableResponse.status} ${disableResponse.statusText}`);
        }
        console.log('Disabled operator:', activeOperator.id);
      }
      // Now enable the selected operator
      let enableUrl;
      if (API_BASE_URL.startsWith('http')) {
        enableUrl = createApiUrl(API_BASE_URL, `rechargeOperator/${operatorId}/true`);
      } else {
        enableUrl = `/admin/v1/api/rechargeOperator/${operatorId}/true`;
      }
      console.log(`Enable URL: ${enableUrl}`);
      const enableResponse = await fetch(enableUrl, {
        method: 'PUT',
        headers: {
          'Authorization': localStorage.getItem('auth'),
          'Content-Type': 'application/json'
        }
      });
      if (!enableResponse.ok) {
        throw new Error(`Failed to enable operator: ${enableResponse.status} ${enableResponse.statusText}`);
      }
      notify('Recharge operator updated successfully', { type: 'success' });
      // Update local state - mark this operator as active and others as inactive
      setOperators(prev => 
        prev.map(op => ({
          ...op,
          isActive: op.id === operatorId
        }))
      );
    } catch (err) {
      console.error('Error updating operator:', err);
      notify('Error updating operator', { type: 'error' });
    }
  };

  // Fetch operators on component mount
  useEffect(() => {
    const fetchOperators = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log('Fetching recharge operators...');
        
        // Use the helper function to create the correct URL
        let fetchUrl;
        if (API_BASE_URL.startsWith('http')) {
          fetchUrl = createApiUrl(API_BASE_URL, 'rechargeOperators');
        } else {
          fetchUrl = '/admin/v1/api/rechargeOperators';
        }
        
        console.log(`API URL: ${fetchUrl}`);
        
        // Make direct API call
        const response = await fetch(fetchUrl, {
          headers: {
            'Authorization': localStorage.getItem('auth'),
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          console.error('API response error:', response.status, response.statusText);
          setError(`Error: ${response.status} ${response.statusText}`);
          setLoading(false);
          return;
        }
        
        const responseData = await response.json();
        console.log('API response:', responseData);
        
        // Handle the API response format
        let operatorData = [];
        if (responseData.data && Array.isArray(responseData.data)) {
          operatorData = responseData.data;
        } else if (Array.isArray(responseData)) {
          operatorData = responseData;
        }
        
        console.log('Processed data:', operatorData);
        
        if (operatorData.length === 0) {
          setError('No operators found');
          setOperators([]);
          return;
        }
        
        // Find the most recently updated operator (active one)
        const sortedOperators = [...operatorData].sort((a, b) => 
          b.updatedAt - a.updatedAt
        );
        const activeOperatorId = sortedOperators[0].id;
        
        // Mark active operator
        setOperators(operatorData);
      } catch (err) {
        console.error('Error fetching operators:', err);
        setError('Failed to load operators. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchOperators();
  }, []);

  // Render loading state
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Render error state
  if (error) {
    return (
      <Card>
        <CardContent>
          <Typography color="error" variant="h6" align="center">
            {error}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Render operators table
  return (
    <Card>
      <CardContent>
        <Typography variant="h5" component="div" gutterBottom>
          Recharge Operator Config
        </Typography>
        
        {operators.length === 0 ? (
          <Typography>No recharge operators found</Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Operator Name</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {operators.map((operator) => (
                  <TableRow key={operator.id}>
                    <TableCell>{operator.name}</TableCell>
                    <TableCell>{formatTimestamp(operator.createdAt)}</TableCell>
                    <TableCell>{formatTimestamp(operator.updatedAt)}</TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={operator.isActive}
                            onChange={() => !operator.isActive && toggleOperator(operator.id)}
                            disabled={operator.isActive}
                            color="primary"
                          />
                        }
                        label={operator.isActive ? "Active" : "Inactive"}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default StandaloneRechargeOperatorList; 